import React, { useEffect, useReducer, useState } from "react";
import {
    Box,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Button,
    IconButton,
    Grid,
    Chip,
    Alert,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    alpha
} from "@mui/material";
import {
    Delete,
    Add,
    ArrowBack,
    Receipt,
    FlightTakeoff,
    Payment,
    Calculate,
    CheckCircle,
    Edit
} from "@mui/icons-material";
import { COLORS, priceFormatter, toEuro } from "../../settings/themeSettings";
import AddBillDialog from "../AddBillDialog";
import AddTicketDialog from "../AddTicketDialog";
import { useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { createFacture, fetchFactures } from "../../data/slices/factureSlice";
import { fetchClient } from "../../data/slices/clientStore";
import { notify } from "../../settings/toastSetting";
import Breadcrumb from "../../components/navigation/Breadcrumb";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateField, LocalizationProvider, frFR } from "@mui/x-date-pickers";
import dayjs from "dayjs";

// Modes de paiement disponibles
const PAYMENT_METHODS_COMPTE = ['compte', 'Chèque', 'CASH', 'Carte de crédit', 'Virement', 'Autre'];
const PAYMENT_METHODS_COMPTANT = ['Chèque', 'CASH', 'Carte de crédit', 'Virement', 'Autre'];

const FactureForm = () => {
    const dispatch = useDispatch();
    const { factures } = useSelector((state) => state.factures);
    const [open, setOpen] = useState(false);
    const [openAddBill, setOpenAddBill] = useState(false);
    const [billets, setBillets] = useState([]);
    const [payments, setPayments] = useState([]);
    const [bills, setBills] = useState([]);

    const [editingTicketIndex, setEditingTicketIndex] = useState(null);
    const [editingBillIndex, setEditingBillIndex] = useState(null);
    const [editingPaymentIndex, setEditingPaymentIndex] = useState(null);
    const [commentaire, setCommentaire] = useState('');
    const [numeroDossier, setNumeroDossier] = useState('');
    const [numeroFacture, setNumeroFacture] = useState(''); // UX-13
    const navigateTo = useNavigate();
    const { clientId } = useParams();
    const isComptant = clientId === 'COMPTANT';
    const [nomClientComptant, setNomClientComptant] = useState('');
    const [adresseClientComptant, setAdresseClientComptant] = useState('');
    const [villeClientComptant, setVilleClientComptant] = useState('');
    const [emailClientComptant, setEmailClientComptant] = useState('');

    const [totalFrais, setTotalFrais] = useState(0);
    const [totalPaiements, setTotalPaiements] = useState(0);
    const [loading, setLoading] = useState(false);
    const [currentClient, setCurrentClient] = useState(null);

    // Chargement initial du client
    useEffect(() => {
        if (clientId && !isComptant) {
            fetchClient(clientId, (err) => console.error(err), (client) => {
                setCurrentClient(client);
            });
        }
    }, [clientId, isComptant]);

    // État du formulaire de paiement inline
    const paymentMethods = isComptant ? PAYMENT_METHODS_COMPTANT : PAYMENT_METHODS_COMPTE;
    const [modePaiement, setModePaiement] = useState(isComptant ? 'CASH' : 'compte');
    const [montantPaiement, setMontantPaiement] = useState('');
    const [datePaiement, setDatePaiement] = useState(dayjs(new Date()));
    const [commentairePaiement, setCommentairePaiement] = useState('');
    const [autreModePaiement, setAutreModePaiement] = useState('');

    const breadcrumbItems = [
        { label: 'Accueil', path: '/' },
        { label: 'Billetterie', path: '/billeterie' },
        { label: 'Factures', path: `/billeterie/factures/${clientId}` },
        { label: 'Nouveau', path: `/billeterie/factures/${clientId}/nouveau` }
    ];

    const [fraisBillet, dispatchFrais] = useReducer((state, action) => {
        if (action.type === "remove") return state - action.value;
        return state + action.value;
    }, 0);

    const handleClose = (ticketInfos) => {
        setOpen(false);
        if (ticketInfos) {
            if (editingTicketIndex !== null) {
                const newBillets = [...billets];
                const oldTarif = newBillets[editingTicketIndex].tarif;
                newBillets[editingTicketIndex] = ticketInfos;
                setBillets(newBillets);
                dispatchFrais({ type: "remove", value: Number(oldTarif) });
                dispatchFrais({ value: Number(ticketInfos.tarif) });
            } else {
                setBillets([...billets, ticketInfos]);
                dispatchFrais({ value: Number(ticketInfos.tarif) });
            }
        }
        setEditingTicketIndex(null);
    };

    const removeBill = (id) => {
        if(window.confirm("Êtes-vous sûr de vouloir supprimer cette ligne ?")) {
            setBills(bills.filter((_, idx) => idx !== id));
        }
    };

    const removePayment = (id) => {
        if(window.confirm("Êtes-vous sûr de vouloir supprimer cette ligne ?")) {
            setPayments(payments.filter((_, idx) => idx !== id));
        }
    };

    const removeTicket = (id) => {
        if(window.confirm("Êtes-vous sûr de vouloir supprimer cette ligne ?")) {
            const ticket = billets[id];
            if (ticket) {
                dispatchFrais({ type: "remove", value: ticket.tarif });
            }
            setBillets(billets.filter((_, idx) => idx !== id));
        }
    };

    const handleAddBillClose = (bill) => {
        setOpenAddBill(false);
        if (bill) {
            if (editingBillIndex !== null) {
                const newBills = [...bills];
                newBills[editingBillIndex] = bill;
                setBills(newBills);
            } else {
                setBills([...bills, bill]);
            }
        }
        setEditingBillIndex(null);
    };

    // Ajouter ou modifier un paiement depuis le formulaire inline
    const addPaymentInline = () => {
        if (!montantPaiement || Number(montantPaiement) <= 0) {
            notify('Veuillez saisir un montant valide', 'warning');
            return;
        }
        const payment = {
            methode: modePaiement === 'Autre' ? autreModePaiement : modePaiement,
            montant: montantPaiement,
            date: datePaiement ? datePaiement.toISOString().split('T')[0] : null,
            commentaire: commentairePaiement
        };

        if (editingPaymentIndex !== null) {
            const newPayments = [...payments];
            newPayments[editingPaymentIndex] = payment;
            setPayments(newPayments);
            setEditingPaymentIndex(null);
        } else {
            setPayments([...payments, payment]);
        }

        setMontantPaiement('');
        setCommentairePaiement('');
        setModePaiement(isComptant ? 'CASH' : 'compte');
        setAutreModePaiement('');
        setDatePaiement(dayjs(new Date()));
    };

    const openEditTicket = (index) => {
        setEditingTicketIndex(index);
        setOpen(true);
    };
    const openEditBill = (index) => {
        setEditingBillIndex(index);
        setOpenAddBill(true);
    };
    const openEditPayment = (index) => {
        setEditingPaymentIndex(index);
        const payment = payments[index];
        const isKnownMethod = paymentMethods.includes(payment.methode);
        setModePaiement(isKnownMethod ? payment.methode : 'Autre');
        if (!isKnownMethod) setAutreModePaiement(payment.methode);
        setMontantPaiement(payment.montant);
        setDatePaiement(payment.date ? dayjs(payment.date) : dayjs(new Date()));
        setCommentairePaiement(payment.commentaire || '');
    };

    const openDialog = () => { setEditingTicketIndex(null); setOpen(true); };
    const openAddBillDialog = () => { setEditingBillIndex(null); setOpenAddBill(true); };

    const addFacture = () => {
        if (!numeroDossier.trim()) {
            notify('Veuillez renseigner la référence du dossier', 'error');
            return;
        }
        if (isComptant && !nomClientComptant.trim()) {
            notify('Veuillez renseigner le nom du client comptant', 'error');
            return;
        }
        if (payments.length === 0) {
            notify('Veuillez saisir au moins un mode de règlement avant de valider', 'warning');
            return;
        }

        setLoading(true);
        const facture = {
            commentaire,
            numeroDossier,
            numeroFacture, // UX-13
            billets,
            frais: bills, // On n'ajoute plus "vente de billets" car c'est déjà compté dans billets
            paiements: payments,
            client: isComptant ? 'COMPTANT' : clientId,
            nomClientComptant: isComptant ? nomClientComptant : null,
            adresseClientComptant: isComptant ? adresseClientComptant : null,
            villeClientComptant: isComptant ? villeClientComptant : null,
            emailClientComptant: isComptant ? emailClientComptant : null
        };

        createFacture(facture, (err) => {
            let errorMessage = "Une erreur s'est produite, actualisez puis réessayez!";
            if (err?.response?.data) {
                if (typeof err.response.data === 'string') {
                    errorMessage = err.response.data;
                } else if (err.response.data.error) {
                    errorMessage = err.response.data.error;
                } else if (err.response.data.message) {
                    errorMessage = err.response.data.message;
                }
            }
            notify(errorMessage, 'error');
            setLoading(false);
        }, (facture) => {
            setLoading(false);
            if (facture.error) { notify(facture.error, 'error'); return; }
            if (!facture.id) { notify("Une erreur s'est produite!", 'error'); return; }
            navigateTo(`/billeterie/factures/${clientId}/details/${facture.id}`);
        });
    };

    useEffect(() => {
        const newTotalFrais = bills.reduce((t, c) => t + Number(c.montant), 0);
        const newTotalPaiements = payments.reduce((t, c) => t + Number(c.montant), 0);
        setTotalFrais(newTotalFrais);
        setTotalPaiements(newTotalPaiements);
    }, [bills, payments]);

    const solde = totalFrais - totalPaiements;
    const isSoldeNegatif = solde < 0;
    const resteAPayer = totalFrais - totalPaiements;

    // Style de section
    const SectionHeader = ({ icon, title, action }) => (
        <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            mb: 2, pb: 1.5, borderBottom: `2px solid ${alpha(COLORS.primary, 0.15)}`
        }}>
            <Typography variant="h6" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {icon}
                {title}
            </Typography>
            {action}
        </Box>
    );

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} localeText={frFR.components.MuiLocalizationProvider.defaultProps.localeText}>
        <Box sx={{
            minHeight: '100vh',
            background: `linear-gradient(135deg, ${alpha(COLORS.primary, 0.04)} 0%, ${alpha(COLORS.secondary, 0.04)} 100%)`,
            p: 3
        }}>
            <Breadcrumb customItems={breadcrumbItems} />

            {/* ── HEADER ── */}
            <Paper elevation={0} sx={{
                p: 3, mb: 3,
                background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.secondary} 100%)`,
                color: 'white', borderRadius: 3, position: 'relative', overflow: 'hidden'
            }}>
                <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.08 }}>
                    <Receipt sx={{ fontSize: 180 }} />
                </Box>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ position: 'relative', zIndex: 1 }}>
                    <IconButton onClick={() => navigateTo(`/billeterie/factures/${clientId}`)}
                        sx={{ bgcolor: alpha('#fff', 0.2), color: 'white', '&:hover': { bgcolor: alpha('#fff', 0.3) } }}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" fontWeight="bold" sx={{ mb: 0.5 }}>Nouvelle Facture</Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            {isComptant ? "Facture client comptant" : `Client #${clientId}`}
                        </Typography>
                    </Box>
                </Stack>
            </Paper>

            {/* ── ÉTAPE 1 : INFORMATIONS ── */}
            <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 3, py: 1.5, bgcolor: alpha(COLORS.primary, 0.06), borderBottom: `1px solid ${alpha(COLORS.primary, 0.1)}` }}>
                    <Typography variant="subtitle1" fontWeight="bold" color={COLORS.primary}>
                        Étape 1 — Informations de la facture
                    </Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        {isComptant && (
                            <>
                                <Grid item xs={12} md={3}>
                                    <TextField label="Nom du client" value={nomClientComptant}
                                        onChange={(e) => setNomClientComptant(e.target.value)}
                                        fullWidth required size="small" placeholder="Nom du client" />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField label="Adresse" value={adresseClientComptant}
                                        onChange={(e) => setAdresseClientComptant(e.target.value)}
                                        fullWidth size="small" placeholder="Adresse complète" />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField label="Ville" value={villeClientComptant}
                                        onChange={(e) => setVilleClientComptant(e.target.value)}
                                        fullWidth size="small" placeholder="Dakar, Abidjan..." />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField label="E-mail" value={emailClientComptant}
                                        onChange={(e) => setEmailClientComptant(e.target.value)}
                                        fullWidth size="small" placeholder="email@exemple.com" type="email" />
                                </Grid>
                            </>
                        )}
                        <Grid item xs={12} md={isComptant ? 3 : 4}>
                            <TextField label="Référence Dossier / Billet" value={numeroDossier}
                                onChange={(e) => setNumeroDossier(e.target.value.toUpperCase())}
                                fullWidth required size="small" placeholder="Saisir la référence manuellement"
                                helperText="Saisie manuelle obligatoire"
                                InputProps={{ startAdornment: <FlightTakeoff sx={{ mr: 1, color: 'action.active', fontSize: '1.1rem' }} /> }} />
                        </Grid>
                        <Grid item xs={12} md={isComptant ? 3 : 4}>
                            <TextField label="N° Facture (Optionnel)" value={numeroFacture}
                                onChange={(e) => setNumeroFacture(e.target.value)}
                                fullWidth size="small" placeholder="Laisser vide pour auto"
                                helperText="Saisie libre alphanumérique"
                                InputProps={{ startAdornment: <Receipt sx={{ mr: 1, color: 'action.active', fontSize: '1.1rem' }} /> }} />
                        </Grid>
                        <Grid item xs={12} md={isComptant ? 3 : 4}>
                            <TextField label="Nature ou Motif" value={commentaire}
                                onChange={(e) => setCommentaire(e.target.value)}
                                fullWidth size="small" placeholder="Notes ou commentaires..." />
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            {/* ── ÉTAPE 2 : BILLETS ── */}
            <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 3, py: 1.5, bgcolor: alpha(COLORS.primary, 0.06), borderBottom: `1px solid ${alpha(COLORS.primary, 0.1)}` }}>
                    <Typography variant="subtitle1" fontWeight="bold" color={COLORS.primary}>
                        Étape 2 — Billets d'avion
                        <Chip label={`${billets.length} billet(s)`} size="small" color="primary" variant="outlined" sx={{ ml: 2 }} />
                    </Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button variant="contained" startIcon={<Add />} onClick={() => setOpen(true)}
                            sx={{ background: `linear-gradient(45deg, ${COLORS.primary}, ${COLORS.secondary})` }}>
                            Ajouter un billet
                        </Button>
                    </Box>
                    {billets.length === 0 ? (
                        <Alert severity="info">Aucun billet ajouté. Cliquez sur "Ajouter un billet" pour commencer.</Alert>
                    ) : (
                        <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${alpha(COLORS.primary, 0.1)}`, borderRadius: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: alpha(COLORS.primary, 0.08) }}>
                                        {['N° Billet', 'Bénéficiaire', 'Trajet', 'Vol Aller', 'Date Aller', 'Vol Retour', 'Date Retour', 'TTC', 'FA', ''].map(h => (
                                            <TableCell key={h} sx={{ fontWeight: 'bold', color: COLORS.primary, fontSize: '0.78rem' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {billets.map((b, idx) => (
                                        <TableRow key={idx} hover>
                                            <TableCell>{b.numero}</TableCell>
                                            <TableCell>{b.nom}</TableCell>
                                            <TableCell>{b.trajet}</TableCell>
                                            <TableCell>{b.numeroVol}</TableCell>
                                            <TableCell>{b.dateVol}</TableCell>
                                            <TableCell>{b.numeroVolRetour || '—'}</TableCell>
                                            <TableCell>{b.dateVolRetour || '—'}</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: COLORS.primary }}>{priceFormatter(b.tarif)}</TableCell>
                                            <TableCell>{priceFormatter(b.montantAchat)}</TableCell>
                                            <TableCell>
                                                <IconButton size="small" color="primary" onClick={() => openEditTicket(idx)}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => removeTicket(idx)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </Paper>

            {/* ── ÉTAPE 3 : FRAIS SUPPLÉMENTAIRES ── */}
            <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 3, py: 1.5, bgcolor: alpha(COLORS.secondary, 0.06), borderBottom: `1px solid ${alpha(COLORS.secondary, 0.1)}` }}>
                    <Typography variant="subtitle1" fontWeight="bold" color={COLORS.secondary}>
                        Étape 3 — Frais supplémentaires (optionnel)
                    </Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button variant="outlined" startIcon={<Add />} onClick={() => setOpenAddBill(true)} size="small"
                            sx={{ borderColor: COLORS.secondary, color: COLORS.secondary }}>
                            Ajouter des frais
                        </Button>
                    </Box>
                    <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${alpha(COLORS.secondary, 0.1)}`, borderRadius: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: alpha(COLORS.secondary, 0.06) }}>
                                    {['Libellé', 'Motif', 'Montant', ''].map(h => (
                                        <TableCell key={h} sx={{ fontWeight: 'bold', color: COLORS.secondary, fontSize: '0.78rem' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {bills.map((bill, idx) => (
                                    <TableRow key={idx} hover>
                                        <TableCell>{bill.libelle}</TableCell>
                                        <TableCell sx={{ color: 'text.secondary' }}>{bill.motif || '—'}</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', color: COLORS.secondary }}>{priceFormatter(bill.montant)}</TableCell>
                                        <TableCell>
                                            <IconButton size="small" color="primary" onClick={() => openEditBill(idx)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => removeBill(idx)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {bills.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ textAlign: 'center', color: 'text.secondary', py: 2, fontStyle: 'italic' }}>
                                            Aucun frais supplémentaire
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Paper>

            {/* ── ÉTAPE 4 : MODE DE RÈGLEMENT (inline, pas de dialog) ── */}
            <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', border: `2px solid ${alpha(COLORS.primary, 0.2)}` }}>
                <Box sx={{ px: 3, py: 1.5, bgcolor: alpha(COLORS.primary, 0.08), borderBottom: `2px solid ${alpha(COLORS.primary, 0.15)}` }}>
                    <Typography variant="subtitle1" fontWeight="bold" color={COLORS.primary} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Payment fontSize="small" />
                        Étape 4 — Mode de règlement
                        {payments.length > 0 && (
                            <Chip icon={<CheckCircle fontSize="small" />} label={`${payments.length} paiement(s)`}
                                size="small" color="success" sx={{ ml: 1 }} />
                        )}
                    </Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Mode de paiement</InputLabel>
                                <Select value={modePaiement} label="Mode de paiement"
                                    onChange={(e) => setModePaiement(e.target.value)}>
                                    {paymentMethods.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Grid>
                        {modePaiement === 'Autre' && (
                            <Grid item xs={12} md={2}>
                                <TextField label="Précisez" value={autreModePaiement}
                                    onChange={(e) => setAutreModePaiement(e.target.value)}
                                    fullWidth size="small" placeholder="Mode de paiement" />
                            </Grid>
                        )}
                        <Grid item xs={12} md={2}>
                            <TextField label="Montant (CFA)" value={montantPaiement} type="number"
                                onChange={(e) => setMontantPaiement(e.target.value)}
                                fullWidth size="small" placeholder="0"
                                helperText={montantPaiement ? `≈ ${toEuro(montantPaiement)} €` : ''} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <DateField label="Date" value={datePaiement}
                                onChange={(e) => setDatePaiement(e)}
                                format="DD-MM-YYYY" fullWidth size="small" />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField label="Commentaire" value={commentairePaiement}
                                onChange={(e) => setCommentairePaiement(e.target.value)}
                                fullWidth size="small" placeholder="Référence, note..." />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button variant="contained" startIcon={editingPaymentIndex !== null ? <Edit /> : <Add />} onClick={addPaymentInline}
                                fullWidth sx={{ bgcolor: COLORS.primary, height: '40px' }}>
                                {editingPaymentIndex !== null ? "Modifier" : "Ajouter"}
                            </Button>
                        </Grid>
                    </Grid>

                    {/* Liste des paiements saisis */}
                    {payments.length > 0 && (
                        <TableContainer component={Paper} elevation={0} sx={{ mt: 2, border: `1px solid ${alpha(COLORS.primary, 0.1)}`, borderRadius: 1 }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: alpha(COLORS.primary, 0.06) }}>
                                        {['Mode', 'Montant', 'Date', 'Commentaire', ''].map(h => (
                                            <TableCell key={h} sx={{ fontWeight: 'bold', color: COLORS.primary, fontSize: '0.78rem' }}>{h}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {payments.map((p, idx) => (
                                        <TableRow key={idx} hover>
                                            <TableCell><Chip label={p.methode} size="small" variant="outlined" color="primary" /></TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: COLORS.primary }}>{priceFormatter(p.montant)}</TableCell>
                                            <TableCell>{p.date}</TableCell>
                                            <TableCell sx={{ color: 'text.secondary' }}>{p.commentaire || '—'}</TableCell>
                                            <TableCell>
                                                <IconButton size="small" color="primary" onClick={() => openEditPayment(idx)}>
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => removePayment(idx)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {payments.length === 0 && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Aucun règlement saisi. La facture ne peut pas être validée sans au moins un mode de paiement.
                        </Alert>
                    )}
                </Box>
            </Paper>

            {/* ── RÉSUMÉ FINANCIER ── */}
            <Paper elevation={2} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 3, py: 1.5, bgcolor: alpha(COLORS.secondary, 0.06), borderBottom: `1px solid ${alpha(COLORS.secondary, 0.1)}` }}>
                    <Typography variant="subtitle1" fontWeight="bold" color={COLORS.secondary} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Calculate fontSize="small" /> Résumé financier
                    </Typography>
                </Box>
                <Box sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(COLORS.primary, 0.05), borderRadius: 2 }}>
                                <Typography variant="body2" color="text.secondary">Total Frais</Typography>
                                <Typography variant="h5" fontWeight="bold" color={COLORS.primary}>{priceFormatter(totalFrais)}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: alpha(COLORS.secondary, 0.05), borderRadius: 2 }}>
                                <Typography variant="body2" color="text.secondary">Total Paiements</Typography>
                                <Typography variant="h5" fontWeight="bold" color={COLORS.secondary}>{priceFormatter(totalPaiements)}</Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: isSoldeNegatif ? alpha('#f44336', 0.08) : alpha('#4caf50', 0.08), borderRadius: 2 }}>
                                <Typography variant="body2" color="text.secondary">Reste à payer</Typography>
                                <Typography variant="h5" fontWeight="bold" color={isSoldeNegatif ? '#f44336' : '#4caf50'}>
                                    {priceFormatter(Math.abs(resteAPayer))} {isSoldeNegatif ? '(Avoir)' : '(Dû)'}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>

            {/* ── BOUTON VALIDATION (après paiement) ── */}
            <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button variant="outlined" size="large" startIcon={<ArrowBack />}
                    onClick={() => navigateTo(`/billeterie/factures/${clientId}`)} sx={{ px: 4 }}>
                    Annuler
                </Button>
                <Button variant="contained" size="large"
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Receipt />}
                    onClick={addFacture}
                    disabled={loading || payments.length === 0}
                    sx={{
                        px: 4,
                        background: payments.length > 0
                            ? `linear-gradient(45deg, ${COLORS.primary}, ${COLORS.secondary})`
                            : undefined
                    }}>
                    {loading ? 'Création en cours...' : 'Valider et créer la facture'}
                </Button>
            </Stack>

            {/* Dialogs */}
            <AddTicketDialog 
                open={open} 
                onClose={handleClose} 
                lastTicket={billets.length > 0 ? billets[billets.length - 1] : null} 
                defaultPnr={numeroDossier}
                defaultNom={currentClient ? `${currentClient.prenom ? currentClient.prenom + ' ' : ''}${currentClient.nom || ''}`.trim() : (nomClientComptant || '')}
                ticket={editingTicketIndex !== null ? billets[editingTicketIndex] : null}
            />
            <AddBillDialog 
                open={openAddBill} 
                onClose={handleAddBillClose} 
                bill={editingBillIndex !== null ? bills[editingBillIndex] : null}
            />
        </Box>
        </LocalizationProvider>
    );
};

export default FactureForm;

